import { useState, useEffect, useMemo, useRef, memo } from 'react';
import '@/styles/terminal-card.css';
import { ASCII_BANNER } from '@/ascii-banner';

interface TerminalCardProps {
	className?: string;
}

const ANIMATION_DELAY_INITIAL = 600;
const ANIMATION_DELAY_COMMAND = 500;
const ANIMATION_DELAY_LINE = 120;

function TerminalCardComponent({ className = '' }: TerminalCardProps) {
	const [isVisible, setIsVisible] = useState(false);
	const [showCommand, setShowCommand] = useState(false);
	const [visibleLines, setVisibleLines] = useState(0);

	const cardRef = useRef<HTMLDivElement>(null);

	const outputLines = useMemo(
		() => ASCII_BANNER.split('\n').filter((line) => line.length > 0),
		[],
	);

	const isComplete = visibleLines >= outputLines.length;

	useEffect(() => {
		const card = cardRef.current;

		if (!card) {
			return;
		}

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry?.isIntersecting && !isVisible) {
					setIsVisible(true);
				}
			},
			{ threshold: 0.3 },
		);

		observer.observe(card);

		return () => observer.disconnect();
	}, [isVisible]);

	useEffect(() => {
		if (!isVisible) {
			return;
		}

		const timer = setTimeout(
			() => setShowCommand(true),
			ANIMATION_DELAY_INITIAL,
		);

		return () => clearTimeout(timer);
	}, [isVisible]);

	useEffect(() => {
		if (!showCommand || visibleLines >= outputLines.length) {
			return;
		}

		const delay =
			visibleLines === 0 ? ANIMATION_DELAY_COMMAND : ANIMATION_DELAY_LINE;

		const timer = setTimeout(() => setVisibleLines((v) => v + 1), delay);

		return () => clearTimeout(timer);
	}, [showCommand, visibleLines, outputLines.length]);

	return (
		<div ref={cardRef} className={`terminal-card-wrapper ${className}`.trim()}>
			<div className="terminal-card">
				<header className="terminal-header">
					<div className="terminal-buttons">
						<span className="terminal-btn terminal-btn-close" />
						<span className="terminal-btn terminal-btn-minimize" />
						<span className="terminal-btn terminal-btn-maximize" />
					</div>
					<span className="terminal-title">me@dev ~</span>
					<div
						className="terminal-buttons"
						aria-hidden="true"
						style={{ visibility: 'hidden' }}
					>
						<span className="terminal-btn" />
						<span className="terminal-btn" />
						<span className="terminal-btn" />
					</div>
				</header>

				<div className="terminal-body">
					<div className="terminal-content">
						{showCommand && (
							<div className="terminal-line terminal-prompt">
								$ curl https://www.larskemper.dev (⬅ try it yourself!)
							</div>
						)}

						{outputLines.slice(0, visibleLines).map((line, i) => (
							<div key={i} className="terminal-line terminal-ascii">
								{line || ' '}
							</div>
						))}

						{isComplete && (
							<div className="terminal-line terminal-input">
								<span className="terminal-prompt">$</span>
								<span className="terminal-cursor-blink">▋</span>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}

export default memo(TerminalCardComponent);
